import React, { useState, useCallback, startTransition } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';


const TopCell = styled('div', {
    shouldForwardProp: (prop) => prop !== 'rowHeight',
})(({ theme, rowHeight = 60 }) => ({
    zIndex: 8,
    position: 'sticky',
    top: 0,
    left: 0,
    height: rowHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.divider}`,
}));

const RowCell = styled('div', {
    shouldForwardProp: (prop) =>
        prop !== 'rowHeight' && prop !== 'isEven' && prop !== 'isSelected',
})(({ theme, rowHeight = 60, isEven, isSelected }) => ({
    zIndex: 8,
    position: 'sticky',
    left: 0,
    height: rowHeight,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    backgroundColor: isEven
        ? theme.palette.neutral.light
        : theme.palette.background.dark,
    cursor: 'pointer',
    userSelect: 'none',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '@keyframes rowBlink': {
        '0%, 100%': { filter: 'brightness(1)' },
        '50%': { filter: 'brightness(1.2)' },
    },
    '&:focus, &:focus-visible': { outline: 'none' },
    '&:hover': { filter: 'brightness(1.1)' },
    ...(isSelected && {
        outline: `1px solid ${theme.palette.primary.main}`,
        outlineOffset: '-1px',
        animation: 'rowBlink 0.8s ease-in-out infinite',
    }),
    '& .MuiTypography-root': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
    },
}));

const VIRT_BUFFER = 5;


/* ======================
        LEFT PANEL
====================== */

const TopLeftPanel = ({
    syncY,
    topLeftScrollRef,
    topRightScrollRef,
    bottomLeftScrollRef,
    rowHeight = 50,
    headerName = "",
    fontSize = '0.75rem',
    data = [],
    onRowClick,
}) => {
    const [selectedId, setSelectedId] = useState(null);
    const [vRange, setVRange] = useState({ start: 0, end: 60 });

    const handleScroll = useCallback((e) => {
        syncY(e.target, [
            topRightScrollRef.current,
            bottomLeftScrollRef.current,
        ]);

        const st = e.target.scrollTop;
        const ch = e.target.clientHeight;
        const newStart = Math.max(0, Math.floor(st / rowHeight) - VIRT_BUFFER);
        const newEnd = Math.min(data.length, Math.ceil((st + ch) / rowHeight) + VIRT_BUFFER);
        startTransition(() => {
            setVRange(prev =>
                prev.start !== newStart || prev.end !== newEnd
                    ? { start: newStart, end: newEnd }
                    : prev
            );
        });
    }, [syncY, topRightScrollRef, bottomLeftScrollRef, rowHeight, data.length]);

    const totalHeight = data.length * rowHeight;
    const visibleData = data.slice(vRange.start, vRange.end);
    const paddingTop = vRange.start * rowHeight;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: "background.default",
            }}
        >
            {/* HEADER */}
            <TopCell rowHeight={rowHeight} sx={{ flexShrink: 0 }}>
                <Typography
                    variant="body1"
                    noWrap
                    sx={{ width: '100%', textAlign: 'center' }}
                >
                    {headerName}
                </Typography>
            </TopCell>

            {/* BODY – SCROLL Y */}
            <Box
                ref={topLeftScrollRef}
                onScroll={handleScroll}
                sx={{
                    position: 'relative',
                    flexGrow: 1,
                    overflowX: 'hidden',
                    overflowY: 'scroll',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {/* Full-height container maintains correct scrollbar size */}
                <Box sx={{ height: totalHeight, position: 'relative' }}>
                    {/* Visible rows absolutely positioned at correct offset */}
                    <Box sx={{ position: 'absolute', top: paddingTop, left: 0, width: '100%' }}>
                        {visibleData.map((row, localIdx) => (
                            <RowCell
                                key={row.id}
                                rowHeight={rowHeight}
                                isEven={(vRange.start + localIdx) % 2 === 0}
                                isSelected={selectedId === row.id}
                                fontSize={fontSize}
                                sx={{ width: '100%' }}
                                onClick={() => {
                                    setSelectedId(prev => prev === row.id ? null : row.id);
                                    onRowClick?.(row);
                                }}
                            >
                                <Typography variant="body2" fontSize={fontSize}>
                                    {row.label}
                                </Typography>
                            </RowCell>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default React.memo(TopLeftPanel);
